<script lang="ts">
  import type { SessionContext, SessionStore } from '$features/session';
  import SessionContextFields from '$ui/components/SessionContextFields.svelte';

  let { store }: { store: SessionStore } = $props();

  // Constante estática: evita crear un objeto nuevo en cada render.
  // Si `store.context` es null, el componente recibe esta referencia
  // estable — sin esto, cada render del padre crea un objeto nuevo y
  // los `$effect` internos del child se disparan en loop.
  const EMPTY_CONTEXT: SessionContext = {
    nodeId: null,
    path: []
  };

  // --- Problem # editable (bloqueado mientras el timer corre) ---
  let problemNumberDraft = $state<string>('');
  let problemNumberEditing = $state(false);

  $effect(() => {
    if (!problemNumberEditing) problemNumberDraft = String(store.problemNumber);
  });

  function commitProblemNumber() {
    const n = Math.max(1, Math.floor(Number(problemNumberDraft)));
    if (Number.isFinite(n) && n >= 1 && n !== store.problemNumber) {
      store.problemNumber = n;
    } else {
      problemNumberDraft = String(store.problemNumber);
    }
    problemNumberEditing = false;
  }

  let problemNumberLocked = $derived(store.problem.state !== 'idle');
</script>

<aside class="session-sidebar" aria-label="Session setup">
  <section class="setup-card">
    <SessionContextFields
      value={store.context ?? EMPTY_CONTEXT}
      onchange={(patch) => store.updateSessionContext(patch)}
      onMutate={() => (store.dbMutationVersion += 1)}
      size="sm"
      showAllContext={true}
    />

    {#if store.context?.nodeId && store.context.nodeId !== store.activeNodeId}
      <button
        type="button"
        class="save-context-btn"
        onclick={() => store.commitContextChange()}
        title="Guardar el tiempo en el nodo actual y cambiar al nuevo"
      >
        Guardar tiempo en el nodo anterior
      </button>
    {/if}

    <div class="field">
      <span class="mini-label">
        Problem #
        <span
          class="help-icon"
          role="img"
          aria-label="Auto-calculated from the current chain. Editable when idle."
          title="Auto-calculated from the current chain (subject → subtopic → book → chapter → section). It updates when you switch context. Editable while the problem timer is idle."
        >?</span>
      </span>
      <input
        type="number"
        min="1"
        class="number-input"
        bind:value={problemNumberDraft}
        onfocus={() => (problemNumberEditing = true)}
        onblur={commitProblemNumber}
        onkeydown={(e) => { if (e.key === 'Enter') (e.currentTarget as HTMLInputElement).blur(); }}
        disabled={problemNumberLocked}
        aria-label="Problem number"
      />
    </div>
  </section>
</aside>

<style>
  .session-sidebar {
    display: flex;
    flex-direction: column;
    gap: var(--space-4);
    width: 100%;
  }

  .setup-card {
    background: var(--color-surface);
    border: var(--border-width) solid var(--color-border);
    border-radius: var(--radius-md);
    padding: var(--space-4);
    display: flex;
    flex-direction: column;
    gap: var(--space-3);
    transition:
      border-color var(--dur-fast) var(--ease-out),
      box-shadow var(--dur-fast) var(--ease-out);
  }

  .setup-card:hover {
    border-color: var(--color-border-strong);
    box-shadow: var(--shadow-sm);
  }

  .field {
    display: flex;
    flex-direction: column;
    gap: var(--space-1);
    min-width: 0;
  }

  .mini-label {
    display: inline-flex;
    align-items: center;
    gap: var(--space-2);
    font-size: 0.65rem;
    font-weight: var(--weight-semibold, 600);
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: var(--color-text-muted);
  }

  /* Tooltip inline (F10 §4.7). Icono ? con title nativo para mostrar
     la explicación en hover sin sumar JS. */
  .help-icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 14px;
    height: 14px;
    border-radius: 50%;
    background: transparent;
    border: 1px solid var(--color-text-muted);
    color: var(--color-text-muted);
    font-size: 0.6rem;
    font-weight: 700;
    letter-spacing: 0;
    line-height: 1;
    cursor: help;
    text-transform: none;
    transition: color var(--dur-fast) var(--ease-out),
      border-color var(--dur-fast) var(--ease-out);
  }

  .help-icon:hover {
    color: var(--color-brand);
    border-color: var(--color-brand);
  }

  .number-input {
    width: 100%;
    height: 36px;
    padding: 0 var(--space-3);
    font-family: var(--font-mono, monospace);
    font-size: var(--text-sm);
    font-variant-numeric: tabular-nums;
    color: var(--color-text-primary);
    background: var(--color-surface);
    border: var(--border-width) solid var(--color-border);
    border-radius: var(--radius-sm);
    outline: none;
    transition: border-color var(--dur-fast) var(--ease-out);
  }

  .number-input:focus-visible {
    border-color: var(--color-accent);
    outline: 2px solid var(--color-accent-tint);
    outline-offset: 0;
  }

  .number-input:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  .save-context-btn {
    width: 100%;
    padding: var(--space-2);
    background: var(--color-brand);
    color: var(--color-brand-fg);
    border: none;
    border-radius: var(--radius-sm);
    font-size: var(--text-sm);
    font-weight: 600;
    cursor: pointer;
    transition: opacity var(--dur-fast) var(--ease-out);
  }
  .save-context-btn:hover {
    opacity: 0.9;
  }


</style>